import { AccountInfo, InteractionRequiredAuthError, IPublicClientApplication } from '@azure/msal-browser';
import { scopes } from '../../../../constants/auth';
import { getBlobTxtUrl, getBlobTxtWriteUrl, getSecondaryBlobTxtWriteUrl } from '../auth/blobConfig';
import { archivarVersionAnterior, InstruccionServicio } from './instruccionesServicio';

export const INTAKE_API_URL =
  String(process.env.REACT_APP_INTAKE_API_URL || '').trim().replace(/^"(.*)"$/, '$1') ||
  'https://ceu-agent-factory-intake.azurewebsites.net/api/factory/intake';

export interface InstructionSnapshot {
  agente?: string;
  archivo: string;
  contenido: string;
  etag: string | null;
  existe: boolean;
  lastModified?: string;
}

export async function acquireInstructionsToken(instance: IPublicClientApplication, accounts: AccountInfo[]): Promise<string> {
  const account = instance.getActiveAccount() || accounts[0];
  if (!account) throw new Error('No se encontró una sesión activa de Microsoft Entra.');
  try {
    return (await instance.acquireTokenSilent({ scopes: [...scopes], account })).accessToken;
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) throw error;
    return (await instance.acquireTokenPopup({ scopes: [...scopes], account })).accessToken;
  }
}

export function instructionApiUrl(file: InstruccionServicio): string {
  if (file.almacenamiento !== 'dominio' || !file.agente) {
    throw new Error('El archivo no tiene un descriptor de almacenamiento de dominio.');
  }
  const url = new URL(INTAKE_API_URL);
  url.pathname = url.pathname.replace(/\/intake\/?$/, '') + `/instructions/${encodeURIComponent(file.agente)}`;
  url.search = new URLSearchParams({ archivo: file.nombre }).toString();
  url.hash = '';
  return url.toString();
}

async function domainResponse(response: Response, file: InstruccionServicio): Promise<InstructionSnapshot> {
  const data = await response.json().catch(() => null);
  if (response.status === 409 || response.status === 412) {
    throw new Error('El archivo ha cambiado desde que lo abriste. Conserva tus cambios y vuelve a cargarlo antes de guardar.');
  }
  if (!response.ok) {
    throw new Error(data?.error || `No se pudo acceder a las instrucciones (${response.status}).`);
  }
  if (data?.agente !== file.agente || data?.archivo !== file.nombre) {
    throw new Error('La respuesta de instrucciones corresponde a otro agente o archivo.');
  }
  if (typeof data?.contenido !== 'string' || typeof data?.existe !== 'boolean' ||
      (data.existe && (typeof data.etag !== 'string' || !data.etag))) {
    throw new Error('Respuesta de instrucciones no válida. Vuelve a cargar el archivo.');
  }
  return data;
}

export async function readInstruction(file: InstruccionServicio, token: () => Promise<string>): Promise<InstructionSnapshot> {
  if (file.almacenamiento === 'dominio') {
    return domainResponse(await fetch(instructionApiUrl(file), {
      cache: 'no-store', headers: { Authorization: `Bearer ${await token()}` },
    }), file);
  }
  const response = await fetch(await getBlobTxtUrl(file.ruta), { cache: 'no-store' });
  if (response.status === 404) return { archivo: file.nombre, contenido: '', etag: null, existe: false };
  if (!response.ok) throw new Error(`No se pudo leer el archivo de instrucciones (${response.status}).`);
  return { archivo: file.nombre, contenido: await response.text(), etag: response.headers.get('ETag'), existe: true,
    lastModified: response.headers.get('Last-Modified') || undefined };
}

export async function saveInstruction(
  serviceId: string, file: InstruccionServicio, contenido: string,
  snapshot: InstructionSnapshot | null, token: () => Promise<string>
): Promise<InstructionSnapshot> {
  if (file.almacenamiento === 'dominio') {
    if (!snapshot || (snapshot.existe && !snapshot.etag)) {
      throw new Error('Vuelve a cargar el archivo antes de guardar para comprobar su versión.');
    }
    if (snapshot.agente !== file.agente || snapshot.archivo !== file.nombre) {
      throw new Error('La versión cargada corresponde a otro agente o archivo. Vuelve a cargar el archivo.');
    }
    return domainResponse(await fetch(instructionApiUrl(file), {
      method: 'PUT',
      headers: { Authorization: `Bearer ${await token()}`, 'Content-Type': 'application/json',
        ...(snapshot.existe ? { 'If-Match': snapshot.etag! } : { 'If-None-Match': '*' }) },
      body: JSON.stringify({ contenido }),
    }), file);
  }
  await archivarVersionAnterior(serviceId, file);
  const urls = await Promise.all([getBlobTxtWriteUrl(file.ruta), getSecondaryBlobTxtWriteUrl(file.ruta)]);
  await Promise.all(urls.map(async (url) => {
    const response = await fetch(url, { method: 'PUT',
      headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'text/plain; charset=utf-8' }, body: contenido });
    if (!response.ok) throw new Error(`Error al guardar en Azure Blob (${response.status}).`);
  }));
  return { archivo: file.nombre, contenido, etag: null, existe: true };
}

/** Invalida los resultados al cambiar de selección, desmontar o iniciar otro guardado. */
export class InstructionSaveScope {
  private generation = 0;
  invalidate(): void { this.generation += 1; }
  begin(): () => boolean {
    const generation = ++this.generation;
    return () => generation === this.generation;
  }
}

export async function saveForCurrentSelection(
  scope: InstructionSaveScope,
  operation: () => Promise<InstructionSnapshot>,
  handlers: {
    success: (snapshot: InstructionSnapshot) => void;
    error: (error: unknown) => void;
    finally: () => void;
  }
): Promise<boolean | null> {
  const isCurrent = scope.begin();
  try {
    const snapshot = await operation();
    if (!isCurrent()) return null;
    handlers.success(snapshot);
    return true;
  } catch (error) {
    if (!isCurrent()) return null;
    handlers.error(error);
    return false;
  } finally {
    if (isCurrent()) handlers.finally();
  }
}
